import { useEffect, useMemo, useRef, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../core/methods/tableCellWidth";
import { useNavigate } from "react-router";
import { all_routes } from "../router/all_routes";
import FiltersTrackCase, {
  FiltersSelectType,
  SearchFormDataType,
  SearchPayloadType,
} from "./FiltersTrackCase";
import Datatable from "../../core/common/dataTable/index";
import TimelineModal from "./TimelineModal";
import { getSearchableHeader } from "../utils/getSearchableHeader";
import useApi from "../../core/customhook/useApi";
import webservice_url, {
  apibaseurl,
} from "../../core/webservice/Webservice_url";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import CaseActionModal from "./work-in-progress/CaseActionModal";
import SnipperLoader from "../../core/common/loader/SnipperLoader";
import { exportAntdTableToExcel } from "../utils/exportExcel";
import "./WorkInProgress.css";
import { filterByColumnSearch } from "../utils/filterByColumnSearch"; 

export interface CaseTableRecord {
  key: string;
  sno: number;
  applicant_name: string;
  case_id: string;
  case_spoc: string;
  employee_id: string;
  case_received_date: string;
  sla_start_date: string;
  due_date: string;
  total_checks: number;
  work_in_progress: number;
  completed: number;
  stop: number;
  insuff_expire: string;
  actions: string;
}

export type TableApiPayload = {
  program_id: string[];
  department_id: string[];
  project_id: string[];
  status: string[];
  assigned_user_id: string[];
  page: number;
  limit: number;
};

const buildTableApiPayload = (
  searchPayload: Partial<SearchPayloadType>,
  page: number,
  limit: number,
  statusId?: string,
): TableApiPayload => ({
  program_id: searchPayload.program ?? [],
  project_id: searchPayload.project ?? [],
  department_id: searchPayload.department ?? [],
  status: statusId ? [statusId] : [],
  assigned_user_id: searchPayload.users ?? [],
  page,
  limit,
});

interface WorkInProgressProps {
  statusId?: string;
}
export const actionOptions = [
  { value: "stop_case", label: "Stop Case" },
  { value: "stop_check", label: "Stop Check" },
  { value: "add_check", label: "Add Check" },
  { value: "generate_report", label: "Generate Report" },
];

const WorkInProgress = ({ statusId }: WorkInProgressProps) => {
  const navigate = useNavigate();
  const [itemList, setItemList] = useState<any[]>([]);

  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });

  const [filterData, setFilterData] = useState<FiltersSelectType>({
    organization: [],
    project: [],
    location: [],
    program: [],
    users: [],
  });
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const [lastSearchPayload, setLastSearchPayload] = useState<
    Partial<SearchPayloadType>
  >({});
  const [pagination, setPagination] = useState({ page: 1, limit: 1000 });

  const getTableData = useApi<any>(
    `/api/v1/admin/track-case-progress/work-in-progress-report?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );
  const caseStatusUpdateAPI = useApi<any>(webservice_url.updateCaseStatus, {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });
  const getCaseStatuses = useApi<any>(webservice_url.GetCaseStatus, {
    method: "GET",
    autoCall: true,
    handleGlobalError: false,
  });
  const DownloadAssestedReport = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
    responseType: "blob",
  });

  const getStatusIdByName = (statusName: string): string | undefined => {
    const statusList = getCaseStatuses?.data?.data ?? [];
    return statusList.find((s: any) => s.status_name === statusName)?.status_id;
  };

  const handleDownloadReport = async (
    assignmentId: any,
    reportType: "assisted" | "non-assisted",
  ) => {
    const assignmentID = assignmentId?.assignment_id;

    try {
      const response: any = await DownloadAssestedReport.callApi(
        undefined,
        `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
      );

      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}_Report_${assignmentID}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(`${reportType} Report Download Error:`, error);
    }
  };

  const inFlightKey = useRef<string>("");

  const fetchTableData = async (
    searchPayload: Partial<SearchPayloadType>,
    page: number,
    limit: number,
  ) => {
    const key = JSON.stringify([searchPayload, page, limit, statusId]);
    if (inFlightKey.current === key) return;
    inFlightKey.current = key;

    try {
      const apiPayload = buildTableApiPayload(
        searchPayload,
        page,
        limit,
        statusId,
      );
      const res = await getTableData.callApi(apiPayload);

      const rows = res?.data?.table ?? [];
      setItemList(
        rows.map((item: any) => ({ ...item, key: item.assignment_id })),
      );

      const paginationInfo = res?.data?.pagination;
      setTotal(paginationInfo?.total ?? 0);
      setPagination({
        page: paginationInfo?.page ?? page,
        limit: paginationInfo?.limit ?? limit,
      });
    } catch (err) {
      console.error(err);
      setItemList([]);
    } finally {
      inFlightKey.current = "";
    }
  };
  useEffect(() => {
    fetchTableData({}, pagination.page, pagination.limit);
  }, [statusId]);

  const filteredData = useMemo(
    () =>
      filterByColumnSearch(
        Array.isArray(itemList) ? itemList : [],
        inventoryColumnSearch,
        {
          assisted_checks: (r) =>
            `${r.verified_assisted_checks ?? 0} / ${r.assisted_checks ?? 0}`,
          non_assisted_checks: (r) =>
            `${r.verified_non_assisted_checks ?? 0} / ${r.non_assisted_checks ?? 0}`,
        },
      ),
    [itemList, inventoryColumnSearch],
  );

  const [stopModalRecord, setStopModalRecord] = useState<any>(null);
  const [stopLoading, setStopLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const handleStop = (record: any) => {
    setStopModalRecord(record);
  };

  const token = localStorage.getItem("userId");

  const handleConfirmStop = async (reason: string) => {
    if (!stopModalRecord) return;
    try {
      setStopLoading(true);

      const stopStatusId = getStatusIdByName("STOP");

      if (!stopStatusId) {
        Swal.fire({
          title: "Error",
          text: `Status "STOP" not found. Please refresh and try again.`,
          icon: "error",
        });
        return;
      }

      await caseStatusUpdateAPI.callApi({
        case_id: stopModalRecord?.case_id,
        status: stopStatusId,
        actor_id: token,
        actor_type: "admin",
        action_reason: reason,
      });

      setItemList((prev) =>
        prev.map((item) =>
          item.assignment_id === stopModalRecord.assignment_id
            ? { ...item, status: "STOP" }
            : item,
        ),
      );

      setStopModalRecord(null);

      await Swal.fire({
        title: "Stopped!",
        text: "The case has been stopped.",
        icon: "success",
        timer: 1500,
        showConfirmButton: true,
      });
    } catch (error) {
      console.error("Stop failed:", error);
      Swal.fire({
        title: "Error",
        text: "Something went wrong. Please try again.",
        icon: "error",
      });
    } finally {
      setStopLoading(false);
    }
  };
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleDownloadDrugReport = async (doc: any, record: any) => {
    const path = doc?.file_name;
    if (!path) return;

    const fileUrl = /^https?:\/\//i.test(path)
      ? path
      : `${apibaseurl}/uploads/${path}`;

    const ext = (doc.original_name || path).split(".").pop();

    const baseName = [record?.candidate_name, record?.case_id]
      .filter(Boolean)
      .join("_")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w\-]/g, "");

    const fileName = baseName
      ? `${baseName}.${ext}`
      : doc.original_name || path;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Drug report download failed", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to download the drug report.",
      });
    }
  };
  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      width: 80,
      align: "center",
    },
    {
      title: getSearchableHeader(
        "Applicant Name",
        "candidate_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "candidate_name",
      key: "candidate_name",
      onCell: () => cellWidth(220),
      render: (text, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a.candidate_name ?? "").localeCompare(b.candidate_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Case ID",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(220),
      sorter: (a, b) => String(a.case_id).localeCompare(String(b.case_id)),
      render: (text: any) => <span className="wip-case-chip">{text}</span>,
    },

    {
      title: getSearchableHeader(
        "Candidate ID",
        "hrms_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "hrms_id",
      key: "hrms_id",
      onCell: () => cellWidth(220),
      sorter: (a, b) => (a.hrms_id ?? "").localeCompare(b.hrms_id ?? ""),
    },
    {
      title: getSearchableHeader("Email", "email", setInventoryColumnSearch),
      dataIndex: "email",
      key: "email",
      onCell: () => cellWidth(220),
      sorter: (a, b) => (a.email ?? "").localeCompare(b.email ?? ""),
    },
    {
      title: getSearchableHeader(
        "Case SPOC",
        "case_spoc_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc_name",
      key: "case_spoc_name",
      onCell: () => cellWidth(220),
      sorter: (a, b) =>
        (a.case_spoc_name ?? "").localeCompare(b.case_spoc_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(220),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(220),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },
    {
      title: getSearchableHeader(
        "Case Received Date",
        "case_receive_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_receive_date",
      key: "case_receive_date",
      onCell: () => cellWidth(220),
      render: (text: string) =>
        text ? (
          <span className="wip-date-cell">
            {dayjs(text).format("DD-MM-YYYY")}
          </span>
        ) : (
          "—"
        ),
      sorter: (a, b) =>
        new Date(a.case_receive_date).getTime() -
        new Date(b.case_receive_date).getTime(),
    },
    {
      title: getSearchableHeader(
        "SLA Start Date",
        "sla_start_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "sla_start_date",
      key: "sla_start_date",
      onCell: () => cellWidth(220),
      render: (text: string) =>
        text ? (
          <span className="wip-date-cell">
            {dayjs(text).format("DD-MM-YYYY")}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: getSearchableHeader(
        "Due Date",
        "due_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "due_date",
      key: "due_date",
      onCell: () => cellWidth(220),
      render: (text: string) =>
        text ? (
          <span className="wip-date-cell">
            {dayjs(text).format("DD-MM-YYYY")}
          </span>
        ) : (
          "—"
        ),
      sorter: (a, b) =>
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime(),
    },
    {
      title: getSearchableHeader(
        "Total Checks",
        "total_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "total_checks",
      key: "total_checks",
      onCell: () => cellWidth(220),
      align: "center",
      sorter: (a, b) => a.total_checks - b.total_checks,
      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },
    {
      title: getSearchableHeader(
        "Assisted",
        "assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "assisted_checks",
      key: "assisted_checks",
      onCell: () => cellWidth(220),
      align: "center",
      sorter: (a, b) => Number(a.assisted_checks) - Number(b.assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.verified_assisted_checks ?? 0);
        const total = Number(record.assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Non-Assisted",
        "non_assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "non_assisted_checks",
      key: "non_assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) =>
        Number(a.non_assisted_checks) - Number(b.non_assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.verified_non_assisted_checks ?? 0);
        const total = Number(record.non_assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Candidate Submited",
        "candidate_submit",
        setInventoryColumnSearch,
      ),
      dataIndex: "candidate_submit",
      key: "candidate_submit",
      onCell: () => cellWidth(160),
      align: "center",
      sorter: (a, b) => a.candidate_submit - b.candidate_submit,
      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },
    {
      title: getSearchableHeader(
        "Work In Progress",
        "work_in_progress_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "work_in_progress_checks",
      key: "work_in_progress_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) => a.work_in_progress_checks - b.work_in_progress_checks,
      render: (value: number) => (
        <span className="wip-chip wip-chip-progress">{value ?? 0}</span>
      ),
    },
    {
      title: getSearchableHeader(
        "Completed",
        "completed_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "completed_checks",
      key: "completed_checks",
      align: "center",
      sorter: (a, b) => a.completed_checks - b.completed_checks,
      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },
    {
      title: getSearchableHeader(
        "Stop",
        "stopped_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "stopped_checks",
      key: "stopped_checks",
      onCell: () => cellWidth(160),
      align: "center",
      sorter: (a, b) => a.stopped_checks - b.stopped_checks,
      render: (value: number) => (
        <span className="wip-chip wip-chip-danger">{value ?? 0}</span>
      ),
    },
    {
      title: "Assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(170),
      render: (_: any, record: any) => (
        <button
          className="wip-btn wip-btn-primary wip-btn-table"
          onClick={() => handleDownloadReport(record, "assisted")}
          disabled={loading || !record.show_assisted_report}
        >
          <i className="bi bi-download"></i>
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },
    {
      title: "Non-Assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(170),
      render: (_: any, record: any) => (
        <button
          className="wip-btn wip-btn-primary wip-btn-table"
          onClick={() => handleDownloadReport(record, "non-assisted")}
          disabled={loading || !record.show_nonassisted_report}
        >
          <i className="bi bi-download"></i>
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },
    {
      title: getSearchableHeader(
        "Drug Test",
        "drug_test_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "drug_test_status",
      key: "drug_test_status",
      align: "center",
      sorter: (a, b) => a.drug_test_status - b.drug_test_status,
    },
    {
      title: "Drug Report",
      dataIndex: "drug_report",
      key: "drug_report",
      align: "center",
      onCell: () => cellWidth(200),
      sorter: (a, b) =>
        (a.drug_report?.length ?? 0) - (b.drug_report?.length ?? 0),
      render: (docs: any[] | null, record: any) =>
        Array.isArray(docs) && docs.length > 0 ? (
          <div className="d-flex flex-column align-items-center gap-1">
            {docs.map((doc) => (
              <div
                key={doc.document_id}
                className="d-flex align-items-center gap-2"
              >
                <span
                  onClick={() => handleDownloadDrugReport(doc, record)}
                  className="wip-chip wip-chip-success"
                  style={{ cursor: "pointer" }}
                >
                  {doc.original_name ?? doc.file_name}{" "}
                  <i className="ti ti-download" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      dataIndex: "",
      onCell: () => cellWidth(170),
      render: (_: any, record: any) =>
        record.status === "STOP" ? (
          <span className="wip-chip wip-chip-neutral">Stopped</span>
        ) : (
          <button
            className="wip-btn wip-btn-outline-danger wip-btn-table"
            onClick={() => handleStop(record)}
          >
            Stop Case
          </button>
        ),
    },
    {
      title: "View Log",
      dataIndex: "",
      onCell: () => cellWidth(120),
      render: (_: any, record: any) => (
        <button
          type="button"
          className="wip-icon-btn"
          onClick={() => handleViewLog(record.case_id)}
          aria-label="View log"
          title="View Log"
        >
          <i className="ti ti-eye"></i>
        </button>
      ),
    },
  ];
  const handleTableChange = (
    page: number,
    limit: number = pagination.limit,
  ) => {
    if (page === pagination.page && limit === pagination.limit) {
      return;
    }
    setPagination({ page, limit });
    fetchTableData(lastSearchPayload, page, limit);
  };
  const loading = getTableData.loading;
  const handleSearch = (payload: SearchPayloadType) => {
    setLastSearchPayload(payload);
    setPagination({ page: 1, limit: pagination.limit });
    fetchTableData(payload, 1, pagination.limit);
  };
  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "work_in_progress_report",
      sheetName: "Work In Progress",
    });
  };
  const handleBulkDrugReportRedirect = () => {
    navigate(all_routes.drugReport);
  };

  return (
    <>
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
        onSearch={handleSearch}
        storageKey="work_in_progress"
      />
      <div className="px-2 wip-page">
        {loading ? (
          <div className="wip-loading-card">
            <SnipperLoader />
          </div>
        ) : (
          <div className="wip-table-card">
            <div className="d-flex gap-2 justify-content-end">

              <button
                type="button"
                className="btn btn-sm btn-primary d-flex gap-2"
                onClick={handleBulkDrugReportRedirect}
              >
                <i className="ti ti-upload"></i>
                <span className="tcp-btn-label">Upload Bulk Drug Reports</span>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary d-flex gap-2"
                onClick={handleDownloadExcel}
                disabled={loading || filteredData.length === 0}
                aria-label="Download Excel"
              >
                <span className="wip-btn-label">Export to Excel</span>
                <i className="bi bi-download"></i>
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              fixedHeader
              tableHeight={650}
              rowKey="assignment_id"
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total,
              }}
              handleTableChange={(pagination: any) => {
                handleTableChange(pagination.current, pagination.pageSize);
              }}
            />
          </div>
        )}
        {/* Timeline Modal */}
        {showTimeline && (
          <TimelineModal
            timelineData={timelineData}
            loading={timelineLoading}
            onClose={() => {
              setShowTimeline(false);
              setTimelineData([]);
            }}
          />
        )}
        {stopModalRecord && (
          <CaseActionModal
            isOpen={!!stopModalRecord}
            title="Stop Case"
            confirmLabel="Stop Case"
            confirmButtonClass="btn-secondary"
            reasonLabel="Enter reason of stopping case"
            onClose={() => setStopModalRecord(null)}
            onConfirm={handleConfirmStop}
            baseFields={[
              { label: "Case ID", value: stopModalRecord.case_id },
              {
                label: "Candidate Name",
                value: stopModalRecord.candidate_name,
              },
              {
                label: "Case Receive Date",
                value: dayjs(stopModalRecord.case_receive_date).format(
                  "DD-MM-YYYY",
                ),
              },
              {
                label: "SLA Start Date",
                value: dayjs(stopModalRecord.sla_start_date).format(
                  "DD-MM-YYYY",
                ),
              },
              {
                label: "Due Date",
                value: dayjs(stopModalRecord.due_date).format("DD-MM-YYYY"),
              },
            ]}
            extraFields={
              [
                // { label: "Stop Reason", value: stopModalRecord.action },
              ]
            }
            loading={stopLoading}
          />
        )}
      </div>
    </>
  );
};

export default WorkInProgress;
